import { FormTypes, MiscTypes } from '@oneblink/types'

import {
  getContactNames,
  getPeople,
  findPerson,
  setToSortedArray,
  getOtherPerson,
  generateName,
  PeopleLike,
} from '../common/contacts'
import {
  findElement,
  replaceElement,
  replaceElementOptions,
  createElementOptions,
} from '../../definition-manipulation'
import { addClass, removeClass } from '../common/classes'

function getAuthorisedContact(
  submission: Record<string, unknown>,
  definition: FormTypes.Form,
) {
  return getOtherPerson(
    {
      Title: 'Title_Authorised__hidden',
      First_Name: 'First_Name_Authorised__hidden',
      Last_Name: 'Last_Name_Authorised__hidden',
      Phone: 'Phone_Authorised__hidden',
      Email: 'Email_Authorised__hidden',
      Job_Function: 'Job_Function_Authorised__hidden',
    },
    submission,
    definition.elements,
  )
}

export function populateContactElements(
  liveSubmission: Record<string, unknown>,
  definition: FormTypes.Form,
  userProfile: MiscTypes.UserProfile | null,
) {
  const contactNames = getContactNames(liveSubmission, definition.elements)

  const contactNamesArray = setToSortedArray(contactNames)

  const usersName = generateName({
    First_Name: userProfile?.firstName,
    Last_Name: userProfile?.lastName,
  })

  if (!contactNamesArray.includes(usersName)) {
    contactNamesArray.unshift(usersName)
  }

  const contactNamesOptions = createElementOptions(contactNamesArray)

  replaceElementOptions(definition, 'Beekeeper_Name', contactNamesOptions)

  replaceElementOptions(definition, 'Authorised_Contact', contactNamesOptions)

  const authorisedContact = getAuthorisedContact(liveSubmission, definition)

  if (authorisedContact?.First_Name || authorisedContact?.Last_Name) {
    contactNames.add(generateName(authorisedContact))
  }

  const personCompletingContactNamesArray = setToSortedArray(contactNames)

  if (!personCompletingContactNamesArray.includes(usersName)) {
    personCompletingContactNamesArray.unshift(usersName)
  }

  replaceElementOptions(
    definition,
    'personCompletingThisFormAndProvidingCertificationS',
    createElementOptions(personCompletingContactNamesArray),
  )

  return definition
}

function getPeopleAndLoggedInUser(
  submission: Record<string, unknown>,
  definition: FormTypes.Form,
  userProfile: MiscTypes.UserProfile | null,
) {
  const people = getPeople(submission, definition.elements)
  if (userProfile) {
    //if the user profile is the same as one of the business contacts don't add
    if (
      !people.some(
        (p) =>
          p.First_Name === userProfile.firstName &&
          p.Last_Name === userProfile.lastName,
      )
    ) {
      people.push({
        First_Name: userProfile.firstName ?? '',
        Last_Name: userProfile.lastName ?? '',
        Phone: userProfile.phoneNumber ?? '',
        Email: userProfile.email ?? '',
      })
    }
  }
  return people
}

export function beekeeperNameDetails(
  submission: Record<string, unknown>,
  definition: FormTypes.Form,
  userProfile: MiscTypes.UserProfile | null,
) {
  const trainingInformation = submission.trainingInformation
  if (Array.isArray(trainingInformation)) {
    const people = getPeopleAndLoggedInUser(submission, definition, userProfile)
    trainingInformation.forEach((trainingInfo) => {
      const personSelected = trainingInfo.Beekeeper_Name

      if (typeof personSelected === 'string' && personSelected !== 'Other') {
        const beekeeper = findPerson(personSelected, people)

        trainingInfo.beekeeperFirst__hidden = beekeeper?.First_Name
        trainingInfo.beekeeperLast__hidden = beekeeper?.Last_Name
        trainingInfo.beekeeperPhone__hidden = beekeeper?.Phone
      } else {
        trainingInfo.beekeeperFirst__hidden = trainingInfo.beekeeperFirst
        trainingInfo.beekeeperLast__hidden = trainingInfo.beekeeperLast
        trainingInfo.beekeeperPhone__hidden = trainingInfo.beekeeperPhone
      }
    })
  }
}

export function authorisedContactDetails(
  submission: Record<string, unknown>,
  definition: FormTypes.Form,
  userProfile: MiscTypes.UserProfile | null,
) {
  const personSelected = submission.Authorised_Contact

  if (typeof personSelected === 'string' && personSelected !== 'Other') {
    const people = getPeopleAndLoggedInUser(submission, definition, userProfile)
    const authorisedContact = findPerson(personSelected, people)

    submission.Title_Authorised__hidden = authorisedContact?.Title
    submission.First_Name_Authorised__hidden = authorisedContact?.First_Name
    submission.Last_Name_Authorised__hidden = authorisedContact?.Last_Name
    submission.Phone_Authorised__hidden = authorisedContact?.Phone
    submission.Email_Authorised__hidden =
      authorisedContact?.Email === 'Other'
        ? authorisedContact?.Other_email
        : authorisedContact?.Email
    submission.Job_Function_Authorised__hidden = authorisedContact?.Job_Function
  } else {
    submission.Title_Authorised__hidden = submission.Title_Authorised
    submission.First_Name_Authorised__hidden = submission.First_Name_Authorised
    submission.Last_Name_Authorised__hidden = submission.Last_Name_Authorised
    submission.Phone_Authorised__hidden = submission.Phone_Authorised
    submission.Email_Authorised__hidden = submission.Email_Authorised
    submission.Job_Function_Authorised__hidden =
      submission.Job_Function_Authorised
  }
}

function getCertifiedPeople(
  submission: Record<string, unknown>,
  definition: FormTypes.Form,
  userProfile: MiscTypes.UserProfile | null,
) {
  const people = getPeopleAndLoggedInUser(submission, definition, userProfile)
  const authorisedContact = getAuthorisedContact(submission, definition)
  if (
    authorisedContact &&
    !people.find(
      (p) =>
        p.First_Name === authorisedContact?.First_Name &&
        p.Last_Name === authorisedContact.Last_Name,
    )
  ) {
    people.push(authorisedContact as PeopleLike)
  }
  return people
}

export function personCompletingFormDetails(
  submission: Record<string, unknown>,
  definition: FormTypes.Form,
  userProfile: MiscTypes.UserProfile | null,
) {
  const personSelected =
    submission.personCompletingThisFormAndProvidingCertificationS

  if (typeof personSelected === 'string' && personSelected !== 'Other') {
    const people = getCertifiedPeople(submission, definition, userProfile)
    const personCompleting = findPerson(personSelected, people)

    submission.First_Name_Certified__copy = personCompleting?.First_Name
    submission.Last_name_Certified__copy = personCompleting?.Last_Name
    submission.Phone_Certified__copy = personCompleting?.Phone
      ? personCompleting.Phone
      : submission.Phone_Certified
    submission.Email_Certified__copy =
      personCompleting?.Email !== 'Other'
        ? personCompleting?.Email
        : personCompleting?.Other_email
    submission.Role_Certified__copy = personCompleting?.Job_Function
      ? personCompleting.Job_Function
      : submission.Role_Certified
  } else {
    submission.First_Name_Certified__copy = submission.First_Name_Certified
    submission.Last_name_Certified__copy = submission.Last_name_Certified
    submission.Phone_Certified__copy = submission.Phone_Certified
    submission.Email_Certified__copy = submission.Email_Certified
    submission.Role_Certified__copy = submission.Role_Certified
  }
}

export function updateCertifiedPersonElements(
  submission: Record<string, unknown>,
  definition: FormTypes.Form,
  userProfile: MiscTypes.UserProfile | null,
) {
  const personSelected =
    submission.personCompletingThisFormAndProvidingCertificationS as
      | string
      | undefined

  const isPersonSelected = typeof personSelected === 'string'

  //default all fields to hidden
  const elementNamesVisibility = {
    First_Name_Certified: false,
    Last_name_Certified: false,
    Email_Certified: false,
    Phone_Certified: false,
    Role_Certified: false,
    First_Name_Certified__copy: false,
    Last_name_Certified__copy: false,
    Email_Certified__copy: false,
    Phone_Certified__copy: false,
    Role_Certified__copy: false,
  }

  //Other selected
  if (isPersonSelected) {
    //in the very very unlikely case we can't find the person allow the user to add details
    if (personSelected.toLowerCase() === 'other') {
      elementNamesVisibility.First_Name_Certified = true
      elementNamesVisibility.Last_name_Certified = true
      elementNamesVisibility.Email_Certified = true
      elementNamesVisibility.Phone_Certified = true
      elementNamesVisibility.Role_Certified = true
    } else {
      const people = getCertifiedPeople(submission, definition, userProfile)

      const person = findPerson(personSelected, people)

      //this is highly unlikely
      if (!person) {
        elementNamesVisibility.First_Name_Certified = true
        elementNamesVisibility.Last_name_Certified = true
        elementNamesVisibility.Email_Certified = true
        elementNamesVisibility.Phone_Certified = true
        elementNamesVisibility.Role_Certified = true
      } else {
        elementNamesVisibility.First_Name_Certified__copy = true
        elementNamesVisibility.Last_name_Certified__copy = true
        elementNamesVisibility.Email_Certified__copy = true
        if (person.Phone) {
          elementNamesVisibility.Phone_Certified__copy = true
        } else {
          elementNamesVisibility.Phone_Certified = true
        }

        if (person.Job_Function) {
          elementNamesVisibility.Role_Certified__copy = true
        } else {
          elementNamesVisibility.Role_Certified = true
        }
      }
    }
  }

  Object.entries(elementNamesVisibility).forEach(([elName, visible]) => {
    const element = findElement(definition, elName)
    if (element && element.parent.elements) {
      element.parent.elements = replaceElement(
        element.parent.elements,
        element.value,
        {
          ...element.value,
          required: visible,
          customCssClasses: visible
            ? removeClass('hidden', element.value.customCssClasses)
            : addClass('hidden', element.value.customCssClasses),
        },
      )
    }
  })
  return definition
}
